import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { BrandMark, Fern } from "@/components/site/BrandMark";
import { Lock, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("A nova senha precisa de pelo menos 8 caracteres.");
    if (password !== confirm) return setError("As senhas não coincidem.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full rounded-xl bg-white border border-[#5B6E5B]/15 pl-11 pr-4 py-3 font-body text-[#2C332A] placeholder:text-[#A8B0A2] outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#C87A5E] focus:ring-2 focus:ring-[#C87A5E]/25";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F0E6] px-5 relative overflow-hidden">
      <Fern color="#5B6E5B" opacity={0.12} className="absolute -left-10 -top-6 w-72" />
      <Fern color="#8A9A82" opacity={0.1} className="absolute -right-16 bottom-0 w-80 scale-x-[-1]" />

      <Link to="/login" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-[#4A5D4E] hover:text-[#2C332A] transition-colors" data-testid="back-to-login">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Voltar ao login
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-[0_20px_60px_rgba(91,110,91,0.12)] border border-[#5B6E5B]/10"
      >
        {done ? (
          <div className="flex flex-col items-center text-center" data-testid="reset-done">
            <div className="grid place-items-center w-14 h-14 rounded-full bg-[#F5F0E6] mb-4">
              <CheckCircle2 className="w-7 h-7 text-[#5B6E5B]" strokeWidth={1.5} />
            </div>
            <h1 className="font-script text-4xl text-[#2C332A]">Senha redefinida</h1>
            <p className="font-body text-sm text-[#7A8A7A] mt-3">Sua nova senha foi salva. Redirecionando para o login…</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center text-center mb-8">
              <BrandMark className="w-14 h-14 mb-3" />
              <h1 className="font-script text-4xl text-[#2C332A]">Nova senha</h1>
              <p className="font-body text-sm text-[#7A8A7A] mt-1">Escolha uma senha forte, com pelo menos 8 caracteres</p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A8A7A]" strokeWidth={1.5} />
                <input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Nova senha" className={input} required minLength={8} />
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A8A7A]" strokeWidth={1.5} />
                <input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repita a nova senha" className={input} required minLength={8} />
              </div>
              {error && <p className="font-body text-sm text-[#C87A5E]" data-testid="reset-error">{error}</p>}
              <button data-testid="reset-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#5B6E5B] px-6 py-3.5 text-[#FDFBF7] transition-[transform,background-color] duration-300 hover:bg-[#4A5D4E] hover:scale-[1.02] disabled:opacity-60">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salvar nova senha"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
